"use client";

import { useCallback, useDeferredValue, useMemo, useState } from "react";
import { Box, Button, HStack, Separator, Text, VStack } from "@chakra-ui/react";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import type { MeiliThought, MindstringPrototype } from "@/generated/fastapi";
import { useMeiliRuntime } from "@/core/search";
import {
  MEILI_SEARCH_CONFIG,
  type MeiliDocumentSearchHit,
  MemoizedSearchIndex,
  MyInfiniteHitsGeneric,
  SearchQueryConnector,
} from "@/features/search";
import { DEFAULT_HITS_PER_PAGE } from "@/features/search/config/meiliSearchConfig";
import {
  SearchClearLanguageFiltersButton,
  SearchHitsPerPageSelector,
  SearchLanguageRefinements,
  SearchSemanticRatioSlider,
} from "@/features/search/components/MeiliSearchControls";
import {
  PROFILE_SHAPING_EMPTY_STATES,
} from "../../config/actions";
import ProfileReviewScopeControls from "../../components/ProfileReviewScopeControls";
import ProfileReviewSelectionActions from "../../components/ProfileReviewSelectionActions";
import {
  countSituationsByPrototype,
  useSituationsQuery,
} from "@/features/situations";
import {
  useAuth,
  useMeiliRefreshState,
  useSearchQuerySelect,
  useSelectSearchControls,
  useUserChoices,
} from "@/state/globalStore";
import { useThoughtActions } from "@/features/thoughts";
import {
  showErrorToast,
  showSuccessToast,
} from "@/shared/feedback";
import type { ThoughtReassignData } from "@/state/globalStoreSlices/userChoices";
import BrandedPrototypeSelector from "@/shared/ui/controls/BrandedPrototypeSelector";
import ContentItemDetailModeHeader from "@/shared/ui/display/ContentItemDetailModeHeader";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import appSettings from "@/shared/config/appSettings";
import {
  mindstringPrototypesSlang,
  normalizeVisibleMindstringPrototype,
} from "@/shared/config/backend";
import { ThoughtAnchorWorkflow } from "@/features/studio";
import { buildNeedsAnchorFilter } from "./needsAnchorFilters";
import NeedsAnchorThoughtHit from "./NeedsAnchorThoughtHit";

type NeedsAnchorReviewMode = "review" | "search_settings";

type NeedsAnchorReviewProps = {
  prototype: MindstringPrototype;
};

export default function NeedsAnchorReview({
  prototype,
}: NeedsAnchorReviewProps) {
  const { isUserAdmin } = useAuth();
  const [selectedPrototype, setSelectedPrototype] =
    useState<MindstringPrototype>(prototype);
  const activePrototype = normalizeVisibleMindstringPrototype(
    selectedPrototype,
    isUserAdmin,
  );
  const { searchClient, searchThoughtDocuments } = useMeiliRuntime({
    surface: "studio_select",
    prototype: activePrototype,
  });
  const { meiliRefreshStamp } = useMeiliRefreshState();
  const searchQueryState = useSearchQuerySelect();
  const searchControls = useSelectSearchControls();
  const situationsQuery = useSituationsQuery();
  const {
    selectedThoughts,
    selectedThoughtsFree,
    selectThought,
    unselectThoughts,
  } = useUserChoices();
  const [anchorItems, setAnchorItems] = useState<ThoughtReassignData[] | null>(
    null,
  );
  const [mode, setMode] = useState<NeedsAnchorReviewMode>("review");
  const [visibleThoughtIds, setVisibleThoughtIds] = useState<string[]>([]);
  const { handleThoughtsDeleteClick } = useThoughtActions();
  const searchConfig = MEILI_SEARCH_CONFIG.thoughts;
  const { mindstringPrototypesSlangItems } = useOptionItems();
  const prototypeLabel = mindstringPrototypesSlang[activePrototype].label;
  const deferredSearchQuery = useDeferredValue(
    searchQueryState.searchQuery.trim(),
  );
  const hasSearchQuery = deferredSearchQuery.length > 0;
  const hasLanguageFilters = searchControls.filters.language.length > 0;
  const defaultThoughtSortBy = searchConfig.sortBy[0]?.value ?? "";
  const hasNonDefaultSearchAdjustments =
    searchControls.hitsPerPage !== appSettings.search.HITS_PER_PAGE ||
    searchControls.sortBy !== defaultThoughtSortBy ||
    searchControls.semanticRatio !== 0.5 ||
    hasLanguageFilters;
  const validSituationIds = useMemo(
    () => (situationsQuery.data ?? []).map((situation) => situation.id),
    [situationsQuery.data],
  );
  const validSituationIdSet = useMemo(
    () => new Set(validSituationIds),
    [validSituationIds],
  );
  const additionalFilters = useMemo(() => {
    const needsAnchorFilter = buildNeedsAnchorFilter(validSituationIds);

    return needsAnchorFilter ? [`(${needsAnchorFilter})`] : [];
  }, [validSituationIds]);
  const selectedThoughtValues = useMemo(() => {
    const selectedThoughtMetadata = new Map<
      string,
      {
        objectID: string;
        situation_id?: string | null;
        prototype: MindstringPrototype;
      }
    >();

    selectedThoughts.forEach((selection) => {
      if (selection.prototype !== activePrototype) {
        return;
      }

      if (
        selection.situation_id &&
        validSituationIdSet.has(selection.situation_id)
      ) {
        return;
      }

      selection.thoughts_ids.forEach((thoughtId) => {
        selectedThoughtMetadata.set(thoughtId, {
          objectID: thoughtId,
          situation_id: selection.situation_id,
          prototype: selection.prototype,
        });
      });
    });

    selectedThoughtsFree.forEach((selection) => {
      if (selection.prototype !== activePrototype) {
        return;
      }

      selection.thoughts_ids.forEach((thoughtId) => {
        if (selectedThoughtMetadata.has(thoughtId)) {
          return;
        }

        selectedThoughtMetadata.set(thoughtId, {
          objectID: thoughtId,
          situation_id: null,
          prototype: selection.prototype,
        });
      });
    });

    return Array.from(selectedThoughtMetadata.values());
  }, [
    activePrototype,
    selectedThoughts,
    selectedThoughtsFree,
    validSituationIdSet,
  ]);
  const selectedVisibleThoughtValues = useMemo(
    () =>
      selectedThoughtValues.filter((thought) =>
        visibleThoughtIds.includes(thought.objectID),
      ),
    [selectedThoughtValues, visibleThoughtIds],
  );
  const selectedVisibleThoughtIds = useMemo(
    () => selectedVisibleThoughtValues.map((thought) => thought.objectID),
    [selectedVisibleThoughtValues],
  );
  const selectedCount = selectedVisibleThoughtValues.length;
  const situationCountsByPrototype = useMemo(
    () => countSituationsByPrototype(situationsQuery.data),
    [situationsQuery.data],
  );
  const prototypeItemsWithCounts = useMemo(
    () =>
      mindstringPrototypesSlangItems.map((item) => ({
        ...item,
        count: situationCountsByPrototype[item.value],
      })),
    [mindstringPrototypesSlangItems, situationCountsByPrototype],
  );

  const handleAnchorSelected = useCallback(() => {
    if (selectedVisibleThoughtValues.length === 0) {
      return;
    }

    setAnchorItems(selectedVisibleThoughtValues);
  }, [selectedVisibleThoughtValues]);
  const handleAnchorSingleThought = useCallback(
    (selectedHit: MeiliDocumentSearchHit) => {
      const selectedThought = selectedHit as MeiliThought;

      setAnchorItems([
        {
          objectID: selectedThought.objectID,
          situation_id: selectedThought.situation_id,
          prototype: selectedThought.prototype,
        },
      ]);
    },
    [],
  );
  const handleReturnToReview = useCallback(() => {
    setAnchorItems(null);
  }, []);
  const handleIndexHitsChange = useCallback(
    (_indexId: string, hitIds: string[]) => {
      setVisibleThoughtIds(hitIds);
    },
    [],
  );
  const handleIndexHitsUnmount = useCallback(() => {
    setVisibleThoughtIds([]);
  }, []);
  const handleSelectVisibleThoughts = useCallback(() => {
    visibleThoughtIds.forEach((thoughtId) => {
      selectThought({
        prototypeCategory: activePrototype,
        situationId: null,
        thoughtId,
      });
    });
  }, [activePrototype, selectThought, visibleThoughtIds]);
  const handleClearSelectedThoughts = useCallback(() => {
    unselectThoughts(selectedVisibleThoughtIds);
  }, [selectedVisibleThoughtIds, unselectThoughts]);
  const handleCopySelectedThoughts = useCallback(async () => {
    if (selectedVisibleThoughtIds.length === 0) {
      showErrorToast("Select thoughts to copy");
      return;
    }

    try {
      const selectedThoughts = await searchThoughtDocuments<MeiliThought>({
        indexName: "thoughts",
        items_ids: selectedVisibleThoughtIds,
      });
      const mindstringsToCopy = selectedVisibleThoughtIds
        .map((thoughtId) =>
          selectedThoughts
            .find((thought) => thought.objectID === thoughtId)
            ?.mindstring?.trim(),
        )
        .filter((mindstring): mindstring is string => Boolean(mindstring));

      if (mindstringsToCopy.length === 0) {
        showErrorToast("Select thoughts to copy");
        return;
      }

      await navigator.clipboard.writeText(mindstringsToCopy.join("\n"));
      showSuccessToast("Copied selected thoughts to clipboard");
    } catch {
      showErrorToast("Failed to copy thoughts");
    }
  }, [searchThoughtDocuments, selectedVisibleThoughtIds]);
  const handleDeleteSelectedThoughts = useCallback(async () => {
    if (selectedVisibleThoughtIds.length === 0) {
      showErrorToast("Select thoughts to delete");
      return;
    }

    await handleThoughtsDeleteClick(
      {
        thought_ids: selectedVisibleThoughtIds,
        delete_mode: "permanent",
        sync_meili_index: "thoughts",
      },
      () => {
        unselectThoughts(selectedVisibleThoughtIds);
      },
      [activePrototype],
    );
  }, [
    activePrototype,
    handleThoughtsDeleteClick,
    selectedVisibleThoughtIds,
    unselectThoughts,
  ]);

  if (!searchClient) {
    return null;
  }

  if (anchorItems && anchorItems.length > 0) {
    return (
      <ThoughtAnchorWorkflow
        items={anchorItems}
        clearSelectionOnSuccess
        onCancel={handleReturnToReview}
        onSuccess={handleReturnToReview}
      />
    );
  }

  if (mode === "search_settings") {
    const handleLanguageToggle = (language: string) => {
      const currentLanguages = searchControls.filters.language;
      const nextLanguages = currentLanguages.includes(language)
        ? currentLanguages.filter((value) => value !== language)
        : [...currentLanguages, language];
      searchControls.setFilters({ language: nextLanguages });
    };
    const handleResetSearchSettings = () => {
      searchControls.resetSearchAdjustments();
      searchQueryState.resetSearchQuery();
    };

    return (
      <VStack align="stretch" spaceY="4">
        <ContentItemDetailModeHeader
          title="Search settings"
          onReturn={() => {
            setMode("review");
          }}
          returnAriaLabel="Back to missing anchors"
        />

        <GenericDrawerInfo
          description="Tune how missing-anchor search behaves inside this maintenance task."
          items={[
            {
              label: hasSearchQuery ? "Search active" : "No search text",
              colorPalette: hasSearchQuery ? "blue" : "gray",
            },
            {
              label: hasNonDefaultSearchAdjustments
                ? "Custom settings"
                : "Default settings",
              colorPalette: hasNonDefaultSearchAdjustments ? "purple" : "gray",
            },
          ]}
        />

        <VStack align="stretch" spaceY="3">
          <HStack justify="flex-end" gap="2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleResetSearchSettings}
              disabled={!hasSearchQuery && !hasNonDefaultSearchAdjustments}
              aria-label="Reset missing-anchor search settings"
            >
              <GenericIcon
                icon="reset"
                highlighted={hasSearchQuery || hasNonDefaultSearchAdjustments}
              />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Reset
              </Text>
            </Button>
          </HStack>

          <SearchSemanticRatioSlider
            stateObject={[searchControls.semanticRatio]}
            stateObjectSetter={(values: number[]) => {
              if (values.length) {
                searchControls.setSemanticRatio(values[0]);
              }
            }}
            title="Semantic-to-keyword search ratio"
            isDecimal={true}
          />

          <SearchHitsPerPageSelector
            value={searchControls.hitsPerPage}
            onChange={searchControls.setHitsPerPage}
            items={DEFAULT_HITS_PER_PAGE.map((item) => ({
              value: parseInt(item.value, 10),
              label: item.label,
            }))}
          />

          {!hasSearchQuery ? (
            <GenericSelector
              previewItems={searchConfig.sortBy}
              stateObject={[searchControls.sortBy]}
              stateObjectSetter={(values: string[]) => {
                if (values.length) {
                  searchControls.setSortBy(values[0]);
                }
              }}
              labelText="Sort by"
              mode="select"
              placeholder="Sort by..."
              disableClear={true}
            />
          ) : null}

          <SearchLanguageRefinements
            selectedLanguages={searchControls.filters.language}
            onLanguageToggle={handleLanguageToggle}
            title="Filter by language"
          />

          <SearchClearLanguageFiltersButton
            onClear={() => {
              searchControls.setFilters({ language: [] });
            }}
            hasFilters={hasLanguageFilters}
          />
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" gap="4">
      <GenericDrawerInfo
        description="Anchor standalone confirmed thoughts to a career situation so they are ready for profile exports."
        items={[
          {
            label: prototypeLabel,
            colorPalette: "purple",
          },
          {
            label: "Confirmed thoughts",
            colorPalette: "blue",
          },
          ...(selectedCount > 0
            ? [
                {
                  label: `${selectedCount} selected`,
                  colorPalette: "green" as const,
                },
              ]
            : []),
        ]}
      />

      <ProfileReviewScopeControls
        controls={
          <BrandedPrototypeSelector
            activePrototype={activePrototype}
            onChange={setSelectedPrototype}
            options={prototypeItemsWithCounts}
            ariaLabel="Choose perspective"
            showCounts={true}
            showDescriptions={true}
            variant="compact"
          />
        }
        onOpenSearchSettings={() => {
          setMode("search_settings");
        }}
        onResetSearchQuery={searchQueryState.resetSearchQuery}
        onSearchQueryChange={searchQueryState.setSearchQuery}
        searchAriaLabel="Search missing profile anchors ... "
        searchPlaceholder="Search missing profile anchors ..."
        searchQuery={searchQueryState.searchQuery}
        searchSettingsHighlighted={
          hasSearchQuery || hasNonDefaultSearchAdjustments
        }
        searchSettingsLabel="Search settings"
      />

      <ProfileReviewSelectionActions
        canSelectAll={visibleThoughtIds.length > 0}
        itemLabelPlural="thoughts"
        itemLabelSingular="thought"
        onClearSelection={handleClearSelectedThoughts}
        onSelectAll={handleSelectVisibleThoughts}
        primaryAction={{
          ariaLabel: "Anchor selected thoughts to a situation",
          icon: "anchor",
          label: "Anchor selected",
          onClick: handleAnchorSelected,
          tone: "primary",
        }}
        secondaryActions={[
          {
            ariaLabel: "Copy selected thoughts",
            icon: "copy_clipboard",
            label: "Copy",
            onClick: handleCopySelectedThoughts,
          },
          {
            ariaLabel: "Delete selected thoughts",
            icon: "delete_permanent",
            label: "Delete",
            onClick: handleDeleteSelectedThoughts,
            tone: "danger",
          },
        ]}
        selectedCount={selectedCount}
      />

      <Separator />

      <Box>
        <InstantSearchNext
          key={meiliRefreshStamp["studio_thoughts"][activePrototype]}
          indexName={searchConfig.indexName}
          searchClient={searchClient}
          routing={false}
          future={{
            preserveSharedStateOnUnmount: true,
          }}
        >
          <SearchQueryConnector
            query={deferredSearchQuery}
            debounceMs={350}
            debugLabel="needs-anchor-review"
          />
          <MemoizedSearchIndex
            indexName={searchConfig.indexName}
            indexId={`needs-anchor-review-${activePrototype}`}
            prototype={activePrototype}
            hitsPerPage={searchControls.hitsPerPage}
            mainContentField={searchConfig.mainContentField}
            previewLength={appSettings.search.SEARCH_RESULT_PREVIEW_LENGTH}
            sortBy={searchControls.sortBy}
            languageFilters={searchControls.filters.language}
            additionalFilters={additionalFilters}
          >
            <MyInfiniteHitsGeneric
              config={searchConfig}
              renderHit={NeedsAnchorThoughtHit}
              indexId={`needs-anchor-review-${activePrototype}`}
              onAnchorAction={handleAnchorSingleThought}
              onIndexHitsChange={handleIndexHitsChange}
              onIndexHitsUnmount={handleIndexHitsUnmount}
              emptyState={{
                ...(hasSearchQuery
                  ? PROFILE_SHAPING_EMPTY_STATES.needsAnchorSearch
                  : PROFILE_SHAPING_EMPTY_STATES.needsAnchor),
                icon: "confirmed_thought",
                variant: "inline",
              }}
            />
          </MemoizedSearchIndex>
        </InstantSearchNext>
      </Box>
    </VStack>
  );
}
